import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, MapPin, Tag, Calendar, FileText, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import StatusProgressLadder from "@/components/StatusProgressLadder";
import { friendlyStatus } from "@/lib/citizen";
import { formatDateTime } from "@/lib/utils-app";

export default function SharedProject() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/projects/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  }
  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Projektet kunde inte hittas</h1>
        <p className="mt-2 text-zinc-500">Länken kan ha återkallats eller är felaktig.</p>
      </div>
    );
  }

  const c = data.project;
  const s = friendlyStatus(c.status);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Delat byggprojekt</div>
      <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500">
        <span>{c.ref}</span>
      </div>
      <h1 className="mt-2 text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{c.title}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-zinc-600">
        {c.address && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.address}</span>}
        <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {c.case_type}</span>
        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Skapat {formatDateTime(c.created_at)}</span>
      </div>

      <div className="mt-8 bg-white border border-zinc-200 rounded-sm p-6 lg:p-8">
        <StatusProgressLadder status={c.status} />
        <div className="mt-6 pt-6 border-t border-zinc-200">
          <div className="text-base font-medium text-zinc-900">{s.label}</div>
          <p className="mt-1 text-sm text-zinc-600">{s.description}</p>
        </div>
      </div>

      {c.description && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-medium mb-3" style={{ fontFamily: "Outfit" }}>Beskrivning</h3>
          <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed">{c.description}</p>
        </div>
      )}

      {c.timeline?.length > 0 && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-medium mb-4" style={{ fontFamily: "Outfit" }}>Tidslinje</h3>
          <ol className="space-y-4">
            {c.timeline.slice().reverse().map((t) => (
              <li key={`${t.at}-${t.status}`} className="relative pl-6">
                <span className="absolute left-0 top-1.5 w-3 h-3 rounded-full bg-blue-50 border-2 border-blue-700" />
                <div className="text-xs text-zinc-500">{formatDateTime(t.at)}</div>
                <div className="text-sm font-medium text-zinc-900">{t.note}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {data.documents?.length > 0 && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-medium mb-4" style={{ fontFamily: "Outfit" }}>Dokument ({data.documents.length})</h3>
          <ul className="space-y-2">
            {data.documents.map((d) => (
              <li key={d.doc_id} className="flex items-center gap-3 text-sm">
                <FileText className="w-4 h-4 text-blue-700" />
                <span>{d.filename}</span>
                <span className="text-xs text-zinc-500">· {formatDateTime(d.uploaded_at)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs text-zinc-500">Filer visas inte publikt – endast filnamn.</div>
        </div>
      )}

      <div className="mt-8 text-center text-xs text-zinc-500">
        Detta är en publik delning av ett byggprojekt via Bygglov. <a href="/" className="text-blue-700 hover:underline">Skapa eget projekt</a>
      </div>
    </div>
  );
}
